import { ReactNode, memo, useMemo } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import BottomPopInMenu from "src/components/BottomPopInMenu";
import FloatingMenuButton from "src/components/FloatingMenuButton";
import { FLOATING_MENU_ITEM_WIDTH } from "src/components/FloatingMenuButton/FloatingMenuButton";
import MultiStageToggle from "src/components/MultiStageToggle";
import Noise from "src/components/Noise/Noise";
import ProfilePicture from "src/components/ProfilePicture";
import { RoundedButtonCircleLargeSize } from "src/components/RoundedButton/constants/RoundedButtonCircleLargeConstants";
import { useGatheringStore } from "src/contexts/GatheringContext";
import { getClientId, useClientId } from "src/helpers/globalHelpers";
import IconAlbum from "src/icons/IconAlbum";
import IconFire from "src/icons/IconFire";
import { paddingMedium, paddingSmall } from "src/styles/spacing";
import { BlurView } from "src/swsh-native";
import useFilterDisplayState from "../../../../hooks/useFilterDisplayState";

const BUBBLE_HORIZONTAL_MARGIN = paddingMedium;
const FloatingFilterMenu = ({
	show,
	menuStyle,
}: {
	show?: boolean;
	menuStyle?: StyleProp<ViewStyle>;
}) => {
	const selectMode = useGatheringStore((state) => state.selectMode);
	const viewMode = useGatheringStore((state) => state.viewMode);
	const clientId = useClientId();
	const { selectedUserId } = useFilterDisplayState();
	const setViewMode = useGatheringStore((state) => state.setViewMode);
	const hasTopPhotos = useGatheringStore((state) => state.hasTopPhotos);

	const items = useMemo(() => {
		const hasFilterPhotos = true;
		const items: {
			node: ReactNode;
			name: "Top" | "All" | "Filter";
		}[] = [];
		if (hasTopPhotos) {
			items.push({
				node: (
					<FloatingMenuButton
						label="Top"
						key="Top"
						testID="FloatingMenuButton-Top"
						onPress={() => {
							setViewMode("Top");
						}}
					>
						<IconFire size={28} />
					</FloatingMenuButton>
				),
				name: "Top",
			});
		}
		items.push({
			node: (
				<FloatingMenuButton
					label="All"
					key="All"
					testID="FloatingMenuButton-All"
					onPress={() => {
						setViewMode("All");
					}}
				>
					<IconAlbum size={28} />
				</FloatingMenuButton>
			),
			name: "All",
		});
		if (hasFilterPhotos) {
			items.push({
				node: (
					<FloatingMenuButton
						label="Filter"
						key="Filter"
						testID="FloatingMenuButton-Filter"
						onPress={() => {
							setViewMode("Filter");
						}}
					>
						<ProfilePicture userId={getClientId()} size={28} touchable={false} />
					</FloatingMenuButton>
				),
				name: "Filter",
			});
		}

		return items;
	}, [hasTopPhotos, setViewMode]);

	const selectedIndex = useMemo(() => {
		if (viewMode === "Top") {
			return items.findIndex((item) => item.name === "Top");
		}
		const isCurUserSelected = !!selectedUserId && selectedUserId === clientId;
		if (isCurUserSelected) {
			return items.findIndex((item) => item.name === "Filter");
		}
		return items.findIndex((item) => item.name === "All");
	}, [clientId, items, selectedUserId, viewMode]);

	return (
		<BottomPopInMenu
			show={show && selectMode === "None"}
			style={menuStyle}
			group="GatheringMenu"
			weight={1000}
		>
			<BlurView
				testID={`FloatingMenuContainer-${items[selectedIndex]?.name}`}
				style={{
					flexDirection: "row",
					flex: 1,
					alignItems: "center",
					borderRadius: 40,
					overflow: "hidden",
					paddingHorizontal: paddingSmall,
					paddingVertical: paddingSmall,
					height: RoundedButtonCircleLargeSize,
				}}
				intensity="floating-menu"
			>
				<Noise
					opacity={0.1}
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
					}}
				/>
				<View
					style={{
						width: items.length * FLOATING_MENU_ITEM_WIDTH,
						marginHorizontal: BUBBLE_HORIZONTAL_MARGIN,
						flexDirection: "row",
						justifyContent: "center",
					}}
				>
					<MultiStageToggle
						selectBubbleStyle={{
							width: "100%",
							borderRadius: 40,
						}}
						bubbleWidth={FLOATING_MENU_ITEM_WIDTH + 2 * BUBBLE_HORIZONTAL_MARGIN}
						selectedIndex={selectedIndex ?? 0}
						Components={items.map((item) => item.node)}
					/>
				</View>
			</BlurView>
		</BottomPopInMenu>
	);
};
export default memo(FloatingFilterMenu);
